import React from 'react';

const ERROR_CATEGORIES = new Set(['TypeError', 'ReferenceError', 'SyntaxError', 'RangeError', 'NetworkError', 'ChunkLoadError']);
let lastReport = 0;
export function diagnosticPage(path = window.location.pathname) {
  if (path.startsWith('/resume/')) return '/resume/editor';
  if (path.startsWith('/payment/')) return '/payment/callback';
  return ['/', '/auth', '/dashboard', '/pricing', '/scanner', '/mock-interview', '/job-fit', '/job-tailor', '/buy-credits'].includes(path) ? path : '/other';
}

// Essential aggregate crash diagnostics, never Meta/GA events. No CV text,
// raw error/stack, tokens, full URL, user identity or device fingerprint.
export async function reportClientError(message, stack, severity = 'error', name = '') {
  try {
    if (Date.now() - lastReport < 10000) return;
    lastReport = Date.now();
    const backend = process.env.REACT_APP_BACKEND_URL?.replace(/\/$/, '') || '';
    await fetch(`${backend}/api/errors/log`, {
      method: 'POST',
      credentials: 'omit',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        category: ERROR_CATEGORIES.has(name) ? name : 'UnhandledError',
        page: diagnosticPage(),
      }),
    });
  } catch {
    // Silent — never let error reporting itself crash the app
  }
}

export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, message: '' };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, message: error?.message || 'Something went wrong' };
  }
  componentDidCatch(error) {
    reportClientError(null, null, 'error', error?.name);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-background flex items-center justify-center p-6" data-testid="error-boundary">
          <div className="max-w-md text-center">
            <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">⚠️</span>
            </div>
            <h1 className="text-xl font-bold mb-2">Something went wrong</h1>
            <p className="text-sm text-muted-foreground mb-6">{this.state.message}</p>
            <p className="text-xs text-muted-foreground mb-4">If this keeps happening, contact support.</p>
            <button
              onClick={() => { window.location.reload(); }}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm"
              data-testid="error-home-btn"
            >
              Reload this page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// Wire up global listeners (call once at app boot)
export function installGlobalErrorReporters() {
  if (typeof window === 'undefined') return;
  if (window.__rr_error_installed) return;
  window.__rr_error_installed = true;

  window.addEventListener('error', (e) => {
    reportClientError(null, null, 'error', e?.error?.name);
  });
  window.addEventListener('unhandledrejection', (e) => {
    const r = e?.reason;
    reportClientError(null, null, 'error', r?.name);
  });
}
